import React, { useState, useContext } from "react";
import { useMutation } from "react-apollo";

import {
  Modal,
  Stack,
  Select,
  TextField,
  Checkbox,
  Popover,
  OptionList,
  Button,
  List,
  DisplayText,
} from "@shopify/polaris";

import Banner from "lib/components/banner/banner";
import AutoComplete from "lib/components/autocomplete/autocomplete";
import constant from "lib/constant/constant";
import baseHelper from "lib/helpers/base";

import { ADD_RULE } from "../../../../../../../apollo/mutations";
import { addProp } from "../props/index";

// import advance shipping context
import { AdvanceShippingContext } from "../../context/context";

const AddNewRule = (props) => {
  const { isVendorAllowed, setBannerOuter } = useContext(AdvanceShippingContext);
  const { addNewRuleModal, setAddNewRuleModal, destinations, band, products, t, refetch, moneyFormat } = props;
  const [addRule, { loading: addRuleLoading }] = useMutation(ADD_RULE);
  const { value: cValue, gql } = constant;
  const [destinationCodes, setDestinationCodes] = useState([]);
  const [isShippingDisabled, setIsShippingDisabled] = useState(false);
  const [shippingBandId, setShippingBand] = useState("");
  const [condition, setCondition] = useState("");
  const [priceType, setPriceType] = useState("");
  const [popoverActive, setPopoverActive] = useState(false);
  const [productIds, setProductIds] = useState([]);
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [ruleBanner, setRuleBanner] = useState({
    isOpen: false,
    title: "",
    message: "",
    status: "",
  });

  const option = {
    priceTypeOptions: [
      { label: t("label.Fixed"), value: constant.FIXED },
      { label: t("label.FreeShipping"), value: constant.FREE },
    ],
    conditionOptions: [
      { label: t("label.Destination"), value: constant.DESTINATION },
      { label: t("label.Products"), value: constant.PRODUCT },
    ],
  };

  const toggleBanner = () => {
    setRuleBanner({ isOpen: false });
  };
  const togglePopoverActive = () => {
    setPopoverActive(!popoverActive);
  };
  const activator = (
    <Button onClick={togglePopoverActive} disclosure>
      {t("label.SelectRegion")}
    </Button>
  );
  const handleShippingBand = (value) => {
    setShippingBand(value);
  };
  const handleShippingDestination = (value) => {
    setDestinationCodes(value);
  };
  const handleChange = () => {
    setAddNewRuleModal(!addNewRuleModal);
  };
  const handleDescriptionChange = (value) => {
    setDescription(value);
  };
  const handleProductChange = (value) => {
    setProductIds(value);
  };
  const handleSelectPriceType = (value) => {
    setPriceType(value);
  };
  const handleSelectShippingRule = (region) => {
    setCondition(region);
  };
  const handleFixedValue = (value) => {
    setPrice(parseFloat(value, 10));
  };
  const addShippingBand = () => {
    const data = { condition, destinationCodes, shippingBandId, productIds, price, priceType, description };
    addRule({ variables: { input: data } })
      .then((response) => {
        const responseError = baseHelper.getResponseError(response.data, gql.CREATE_SHIPPING_RULE);
        if (responseError) {
          setRuleBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
          return;
        }
        refetch();
        setBannerOuter({ isOpen: true, title: t("Rule is added successfully"), status: "success" });
        setAddNewRuleModal(false);
      })
      .catch(() => {
        setRuleBanner({ isOpen: true, status: constant.CRITICAL, title: t("error.common.somethingWentWrong") });
      });
  };
  const checkValidation = () => {
    if (!shippingBandId) {
      return t("error.advanceShipping.SelectShippingBand");
    }
    if (!condition) {
      return t("error.advanceShipping.SelectShippingCondition");
    }
    if (condition === constant.DESTINATION && !(destinationCodes && destinationCodes.length)) {
      return t("error.advanceShipping.SelectAtleastOneDestination");
    }
    if (condition === constant.PRODUCT && !(productIds && productIds.length)) {
      return t("error.advanceShipping.SelectAtleastOneProduct");
    }
    if (!description) {
      return t("error.advanceShipping.AddDescriptionForRule");
    }
    if (description.length > 255) {
      return t("error.advanceShipping.DescriptionCharacter");
    }
    if (!priceType) {
      return t("error.advanceShipping.ProvidePriceType");
    }
    if (priceType !== constant.FREE && !price) {
      return t("error.advanceShipping.ProvidePrice");
    }
    if (priceType !== constant.FREE && price < 0) {
      return t("error.advanceShipping.ProvideValidPrice");
    }
    return false;
  };

  const handleSubmit = () => {
    const hasError = checkValidation();
    if (hasError) {
      setRuleBanner({
        isOpen: true,
        title: hasError,
        status: constant.CRITICAL,
      });
      return false;
    }
    addShippingBand();
    return true;
  };
  const togglePopover = () => setPopoverActive(!popoverActive);

  const renderRegion = () => {
    const list = destinations.map((item) => {
      const { title, options = [] } = item;
      const selectedOptions = options.filter((key) => destinationCodes.includes(key.value));
      if (!selectedOptions.length) {
        return null;
      }
      return (
        <List.Item key={`${item.title}${selectedOptions.length}`}>
          {title}
          <List>
            {selectedOptions.map((key) => (
              <List.Item key={key.value}>{key.label}</List.Item>
            ))}
          </List>
        </List.Item>
      );
    });
    const destinationList = list.filter((item) => item);

    if (!destinationList.length) {
      return null;
    }

    return (
      <Stack>
        <Stack.Item>
          <DisplayText>{`${t("label.SelectedDestinations")}:`}</DisplayText>
        </Stack.Item>
        <Stack.Item>
          <List>{destinationList}</List>
        </Stack.Item>
      </Stack>
    );
  };

  if (!isVendorAllowed) {
    return null;
  }

  return (
    <>
      <Modal
        open={addNewRuleModal}
        onClose={handleChange}
        title={t("label.AddNewRule")}
        primaryAction={{
          content: t("label.Add"),
          loading: addRuleLoading,
          onAction: handleSubmit,
        }}
      >
        <Modal.Section>
          <Stack vertical>
            <Stack.Item>
              {ruleBanner.isOpen && (
                <Banner
                  isOpen={ruleBanner.isOpen}
                  title={ruleBanner.title}
                  message={ruleBanner.message}
                  status={ruleBanner.status}
                  onDismiss={toggleBanner}
                />
              )}
            </Stack.Item>
            <Stack.Item>
              <Select
                label={t("label.SelectShippingBand")}
                placeholder={t("placeHolder.SelectShippingBand")}
                options={band}
                onChange={handleShippingBand}
                value={shippingBandId}
              />
            </Stack.Item>
            <Stack.Item>
              <Select
                label={t("label.SelectShippingRuleCondition")}
                options={option.conditionOptions}
                onChange={handleSelectShippingRule}
                value={condition}
                placeholder={t("placeHolder.SelectShippingRuleCondition")}
              />
            </Stack.Item>
            {condition === constant.PRODUCT && (
              <Stack.Item>
                <AutoComplete
                  label=""
                  key={products.value}
                  values={products}
                  onChange={handleProductChange}
                  selected={productIds}
                  placeholder={t("placeHolder.StartTypingTitle")}
                  minimumSearchLength={Number(3)}
                />
              </Stack.Item>
            )}

            {condition === constant.DESTINATION && (
              <Stack.Item>
                <Stack.Item>
                  <Popover active={popoverActive} activator={activator} onClose={togglePopover}>
                    <OptionList
                      onChange={(value) => {
                        handleShippingDestination(value);
                      }}
                      sections={destinations}
                      selected={destinationCodes}
                      allowMultiple
                    />
                  </Popover>
                </Stack.Item>
                <Stack.Item>{renderRegion()}</Stack.Item>
              </Stack.Item>
            )}

            <Stack.Item>
              <TextField
                label={t("label.Description")}
                value={description}
                onChange={handleDescriptionChange}
                placeholder={t("placeHolder.Description")}
              />
            </Stack.Item>
            <Stack.Item>
              <Stack>
                <Stack.Item>
                  <Select
                    label={t("label.FixedOrFreeShipping")}
                    options={option.priceTypeOptions}
                    onChange={handleSelectPriceType}
                    value={priceType}
                    placeholder={t("placeHolder.SelectShippingRate")}
                  />
                </Stack.Item>
                {priceType === constant.FIXED && (
                  <Stack.Item>
                    <TextField
                      prefix={moneyFormat}
                      label={t("label.Amount")}
                      type="number"
                      onChange={handleFixedValue}
                      value={price.toString() || ""}
                      min={cValue.MIN_PRICE}
                      step={cValue.STEP}
                    />
                  </Stack.Item>
                )}
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Checkbox
                label={t("label.NotAvailableForShipping")}
                checked={isShippingDisabled}
                onChange={() => {
                  setIsShippingDisabled(!isShippingDisabled);
                }}
              />
            </Stack.Item>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
};
AddNewRule.propTypes = addProp.type;
export default AddNewRule;
